import type { Booking, CustomerRecord, Piece } from '@/domain/types';

export interface InspirationPhoto {
  url: string;
  source: 'design-intent' | 'conversation';
  caption?: string;
  /** When the photo arrived (conversation timestamp). Absent for design-intent refs. */
  capturedAt?: string;
}

export interface ProgressEntry {
  id: string;
  url: string;
  capturedAt: string;
  pieceId: string;
  bookingId?: string;
  caption?: string;
}

export function inspirationFor(customer: CustomerRecord | undefined): InspirationPhoto[] {
  if (!customer) return [];
  const seen = new Set<string>();
  const out: InspirationPhoto[] = [];

  for (const url of customer.designIntent?.referencePhotos ?? []) {
    if (seen.has(url)) continue;
    seen.add(url);
    out.push({ url, source: 'design-intent' });
  }

  for (const entry of customer.conversation ?? []) {
    for (const att of entry.attachments ?? []) {
      if (att.kind !== 'image') continue;
      if (seen.has(att.url)) continue;
      seen.add(att.url);
      out.push({ url: att.url, source: 'conversation', capturedAt: entry.timestamp });
    }
  }
  return out;
}

export function progressFor(
  customer: CustomerRecord | undefined,
  pieces: Record<string, Piece>,
): ProgressEntry[] {
  if (!customer) return [];
  const out: ProgressEntry[] = [];
  const seenPieceIds = new Set<string>();
  const considerPiece = (piece: Piece | undefined) => {
    if (!piece || seenPieceIds.has(piece.id)) return;
    seenPieceIds.add(piece.id);
    for (const photo of piece.progressPhotos ?? []) {
      out.push({
        id: photo.id,
        url: photo.url,
        capturedAt: photo.capturedAt,
        pieceId: piece.id,
        bookingId: photo.bookingId,
        caption: photo.caption,
      });
    }
  };
  for (const pieceId of customer.pieces ?? []) considerPiece(pieces[pieceId]);
  for (const piece of Object.values(pieces)) {
    if (piece.customerId === customer.id) considerPiece(piece);
  }
  out.sort((a, b) => (a.capturedAt < b.capturedAt ? 1 : -1));
  return out;
}

/** Newest-first; undated photos sort last, keeping their original order. */
function byNewestFirst<T extends { capturedAt?: string }>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ta = a.item.capturedAt;
      const tb = b.item.capturedAt;
      if (ta && tb) return ta === tb ? a.index - b.index : ta < tb ? 1 : -1;
      if (ta) return -1;
      if (tb) return 1;
      return a.index - b.index;
    })
    .map((x) => x.item);
}

/**
 * Annotate a reference pool so photos already used on an earlier booking sit
 * behind the picker's "See all photos" filter rather than in the default view.
 *
 * Two properties matter, and both are why this exists alongside
 * `partitionReferences`:
 *
 * 1. Hiding depends ONLY on booking history, never on the current selection. The
 *    brief's selection lives in `designIntent.referencePhotos`, which
 *    `inspirationFor` also reads as a pool source, so selection-dependent hiding
 *    made tiles jump (and vanish) as the agent ticked them.
 * 2. The used-before photos are appended AFTER the current ones, so switching the
 *    filter on only ever adds to the end. Nothing already on screen moves.
 */
export function buildReferenceGroups({
  photos,
  bookings,
  labelForBooking,
}: {
  photos: ReferenceCandidate[];
  bookings: Booking[];
  labelForBooking: (booking: Booking) => string;
}): PartitionedReferences {
  // Most recent earlier booking per url.
  const usesByUrl = new Map<string, Booking>();
  const byRecency = [...bookings].sort((a, b) => (a.start < b.start ? 1 : a.start > b.start ? -1 : 0));
  for (const b of byRecency) {
    for (const url of b.referencePhotos ?? []) {
      if (!usesByUrl.has(url)) usesByUrl.set(url, b);
    }
  }

  const current: ReferencePhotoView[] = [];
  const usedBefore: ReferencePhotoView[] = [];
  for (const photo of photos) {
    const usedOn = usesByUrl.get(photo.url);
    const view: ReferencePhotoView = {
      url: photo.url,
      caption: photo.caption,
      capturedAt: photo.capturedAt,
      hiddenByDefault: Boolean(usedOn),
      usedOnLabel: usedOn ? labelForBooking(usedOn) : undefined,
    };
    (usedOn ? usedBefore : current).push(view);
  }

  // Current first, used-before appended: turning the filter on grows the grid
  // from the end instead of reshuffling it.
  return {
    photos: [...byNewestFirst(current), ...byNewestFirst(usedBefore)],
    hiddenCount: usedBefore.length,
  };
}

/** A candidate reference photo fed into the picker (structurally a PhotoGalleryItem). */
export interface ReferenceCandidate {
  url: string;
  caption?: string;
  /** ISO timestamp; drives most-recent-first ordering. Undated photos sort last. */
  capturedAt?: string;
}

/** A candidate annotated with how the reference picker should treat it. */
export interface ReferencePhotoView extends ReferenceCandidate {
  /**
   * Hidden from the default "suggested" view because it's already been used on
   * another of the customer's bookings (and isn't exempt via same-piece / selection).
   */
  hiddenByDefault: boolean;
  /** Badge text naming the other booking this photo was used on, if any. */
  usedOnLabel?: string;
}

export interface PartitionReferencesOptions {
  /** The full pool of photos the client sent. */
  photos: ReferenceCandidate[];
  /** The customer's bookings. The one being edited (currentBookingId) is excluded internally. */
  bookings: Booking[];
  /** Id of the booking being edited; its own refs never count as "used elsewhere". Omit when creating. */
  currentBookingId?: string;
  /** Piece the new/edited booking belongs to; photos reused across this piece stay in the default view. */
  currentPieceId?: string;
  /** URLs already selected on the booking being edited — always shown regardless of use elsewhere. */
  selectedUrls?: string[];
  /** Formats the "used on <booking>" badge for the booking a photo was last used on. */
  labelForBooking: (booking: Booking) => string;
}

export interface PartitionedReferences {
  /** Every photo, most-recent-first, annotated for the picker. */
  photos: ReferencePhotoView[];
  /** How many photos are hidden from the default view. */
  hiddenCount: number;
}

/**
 * Split the reference pool into a suggested (default) view and the used/earlier
 * photos tucked behind "See all". A photo is hidden by default only when it's
 * already attached to another of the customer's bookings — unless that booking
 * belongs to the current piece (continuing sessions legitimately reuse refs) or
 * the photo is already selected on the booking being edited. Pure + presentational:
 * the picker just renders what this returns.
 */
export function partitionReferences({
  photos,
  bookings,
  currentBookingId,
  currentPieceId,
  selectedUrls,
  labelForBooking,
}: PartitionReferencesOptions): PartitionedReferences {
  const selected = new Set(selectedUrls ?? []);
  const otherBookings = bookings.filter((b) => b.id !== currentBookingId);

  // For each url, the other bookings that already use it (most-recent-first by start).
  const usesByUrl = new Map<string, Booking[]>();
  for (const b of otherBookings) {
    for (const url of b.referencePhotos ?? []) {
      const list = usesByUrl.get(url) ?? [];
      list.push(b);
      usesByUrl.set(url, list);
    }
  }
  for (const list of usesByUrl.values()) {
    list.sort((a, b) => (a.start < b.start ? 1 : a.start > b.start ? -1 : 0));
  }

  const views: ReferencePhotoView[] = photos.map((photo) => {
    const uses = usesByUrl.get(photo.url) ?? [];
    const usedOnSamePiece =
      currentPieceId != null && uses.some((b) => b.pieceId === currentPieceId);
    const hiddenByDefault =
      uses.length > 0 && !selected.has(photo.url) && !usedOnSamePiece;
    return {
      url: photo.url,
      caption: photo.caption,
      capturedAt: photo.capturedAt,
      hiddenByDefault,
      usedOnLabel: uses.length > 0 ? labelForBooking(uses[0]!) : undefined,
    };
  });

  // Most-recent-first; undated photos sort last, preserving their original order.
  const withIndex = views.map((view, index) => ({ view, index }));
  withIndex.sort((a, b) => {
    const ta = a.view.capturedAt;
    const tb = b.view.capturedAt;
    if (ta && tb) {
      if (ta !== tb) return ta < tb ? 1 : -1;
    } else if (ta) {
      return -1;
    } else if (tb) {
      return 1;
    }
    return a.index - b.index;
  });

  const ordered = withIndex.map((x) => x.view);
  return { photos: ordered, hiddenCount: ordered.filter((p) => p.hiddenByDefault).length };
}
